import React, { useState } from 'react';
import { ListChecks, Plus, Trash2, Eye, AlertTriangle, Shield, RefreshCw } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отслеживания важных доменов
 */
const DomainsToWatch = ({ domains, onSaveDomain, onDeleteDomain, onCheckDomain }) => {
  const [newDomain, setNewDomain] = useState('');
  const [newDomainName, setNewDomainName] = useState('');
  const [isAddingDomain, setIsAddingDomain] = useState(false);
  const [error, setError] = useState('');

  // Обработчик добавления нового домена
  const handleAddDomain = () => {
    if (!newDomain.trim()) {
      setError('Пожалуйста, введите домен для отслеживания');
      return;
    }

    // Валидация домена
    const domainPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
    if (!domainPattern.test(newDomain)) {
      setError('Введите корректный домен');
      return;
    }

    // Проверка на дубликаты
    if (domains.some(domain => domain.domain === newDomain)) {
      setError('Этот домен уже добавлен для отслеживания');
      return;
    }

    // Добавление домена
    onSaveDomain(newDomain, newDomainName || `Домен ${newDomain}`);
    
    // Сброс формы
    setNewDomain('');
    setNewDomainName('');
    setIsAddingDomain(false);
    setError('');
  };

  // Извлечение базового домена для отображения
  const extractBaseDomain = (domain) => {
    try {
      if (!domain) return domain;
      
      // Удаляем http:// или https:// если они есть
      let cleanDomain = domain.replace(/^https?:\/\//i, '');
      
      // Удаляем www. если оно есть
      cleanDomain = cleanDomain.replace(/^www\./i, '');
      
      // Удаляем путь после домена
      cleanDomain = cleanDomain.split('/')[0];
      
      return cleanDomain;
    } catch (e) {
      return domain;
    }
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return 'Никогда';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <ListChecks className="w-5 h-5 mr-2 text-indigo-500" />
        Отслеживаемые домены
      </h3>

      {/* Список доменов */}
      {domains && domains.length > 0 ? (
        <div className="space-y-3 mb-4 max-h-80 overflow-y-auto custom-scrollbar pr-1">
          {domains.map((domain) => (
            <div 
              key={domain.id} 
              className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <div className="flex items-start justify-between">
                <div className="flex-grow">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {domain.name}
                  </h4>
                  <div className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">
                    {extractBaseDomain(domain.domain)}
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => onCheckDomain(domain.domain)}
                    className="p-1 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400"
                    title="Проверить домен"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDeleteDomain(domain.id)}
                    className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                    title="Удалить из отслеживаемых"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 flex items-center">
                <span className="mr-2">Добавлен: {formatDate(domain.dateAdded)}</span>
                {domain.status === 'warning' && (
                  <AlertTriangle className="w-4 h-4 text-orange-500" title="Есть проблемы с редиректами" />
                )}
                {domain.status === 'error' && (
                  <AlertTriangle className="w-4 h-4 text-red-500" title="Обнаружены ошибки" />
                )}
                {domain.status === 'ok' && (
                  <Shield className="w-4 h-4 text-green-500" title="Всё в порядке" />
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Добавьте важные домены для регулярной проверки редиректов.
        </div>
      )}

      {/* Форма добавления домена */}
      {isAddingDomain ? (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/30">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            Добавить домен для отслеживания
          </h4>
          
          <div className="space-y-3">
            <div>
              <input
                type="text"
                placeholder="Название (например, 'Корпоративный сайт')"
                className="form-input w-full mb-2"
                value={newDomainName}
                onChange={(e) => setNewDomainName(e.target.value)}
              />
              
              <input
                type="text"
                placeholder="URL домена (например, example.com)"
                className={`form-input w-full ${error ? 'input-error' : ''}`}
                value={newDomain}
                onChange={(e) => {
                  setNewDomain(e.target.value);
                  setError('');
                }}
              />
              
              {error && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {error}
                </p>
              )}
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsAddingDomain(false);
                  setNewDomain('');
                  setNewDomainName('');
                  setError('');
                }}
              >
                Отмена
              </Button>
              <Button
                type="button"
                primary
                size="sm"
                icon={<Plus className="w-4 h-4" />}
                onClick={handleAddDomain}
              >
                Добавить
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          icon={<Plus className="w-4 h-4" />}
          onClick={() => setIsAddingDomain(true)}
          fullWidth
        >
          Добавить домен
        </Button>
      )}

      {/* Блок с информацией */}
      {domains && domains.length > 0 && (
        <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
          <div className="flex items-start">
            <RefreshCw className="w-4 h-4 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
            <div className="ml-2">
              <h4 className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                Регулярная проверка
              </h4>
              <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">
                Регулярно проверяйте важные домены, чтобы контролировать корректность редиректов и обнаруживать проблемы SEO на ранних этапах.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainsToWatch;